import { createAxiosInstance } from './axios';

/**
 * Resume Ranking API Service
 * Provides functionality to rank resumes and get detailed analysis
 */

/**
 * Analyzes and ranks a resume
 * @param {number|string} resumeId - The ID of the resume to analyze
 * @param {Function} getToken - Optional Clerk getToken function for authentication
 * @returns {Promise<Object>} Response containing grade, score, justification, and more
 */
export const rankResume = async (resumeId, getToken = null) => {
  try {
    const axiosInstance = createAxiosInstance(getToken);
    
    const response = await axiosInstance.post('/resume-ranking/', {
      resume_id: resumeId
    });

    return response.data;
  } catch (error) {
    console.error('Resume ranking failed:', error);
    throw error;
  }
};

/**
 * Analyzes and ranks a resume with detailed error handling
 * @param {number|string} resumeId - The ID of the resume to analyze
 * @param {Function} getToken - Optional Clerk getToken function for authentication
 * @returns {Promise<Object>} Response containing analysis results or error details
 */
export const rankResumeWithErrorHandling = async (resumeId, getToken = null) => {
  try {
    // Validate resume ID
    if (!resumeId) {
      throw new Error('Resume ID is required');
    }
    
    // Ensure resumeId is a number or string that can represent an ID
    const processedResumeId = typeof resumeId === 'number' ? resumeId : parseInt(resumeId);
    
    if (isNaN(processedResumeId) || processedResumeId <= 0) {
      throw new Error('Resume ID must be a valid positive number');
    }
    
    console.log('Processing resume with ID:', processedResumeId);

    const result = await rankResume(processedResumeId, getToken);
    
    // Validate response structure
    if (!result || typeof result !== 'object') {
      throw new Error('Invalid response format from resume ranking API');
    }

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('Resume ranking error:', error);
    
    let errorMessage = 'Failed to analyze resume';
    
    if (error.response) {
      // API responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          errorMessage = data?.message || 'Invalid resume ID provided';
          break;
        case 401:
          errorMessage = 'Authentication required to access resume ranking';
          break;
        case 403:
          errorMessage = 'You do not have permission to access resume ranking';
          break;
        case 429:
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 500:
          errorMessage = 'Server error occurred while analyzing resume';
          break;
        default:
          errorMessage = data?.message || `Request failed with status ${status}`;
      }
    } else if (error.request) {
      // Network error
      errorMessage = 'Network error. Please check your connection and try again';
    } else if (error.message) {
      // Other error
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      originalError: error
    };
  }
};

/**
 * Utility function to extract specific information from resume ranking response
 * @param {Object} rankingResult - The result from rankResume function
 * @returns {Object} Extracted key information in a simplified format
 */
export const extractResumeInsights = (rankingResult) => {
  if (!rankingResult || typeof rankingResult !== 'object') {
    return null;
  }

  return {
    // Core ranking information
    grade: rankingResult.grade || 'N/A',
    rawScore: rankingResult.raw_score || 0,
    
    // Analysis details
    justification: rankingResult.justification || '',
    gaps: rankingResult.gaps_compared_to_target_job || [],
    
    // Interview preparation
    interviewScript: rankingResult.suggested_interview_script || '',
    
    // Inferred job profile
    jobProfile: {
      title: rankingResult.inferred_job_profile?.job_title || 'N/A',
      skills: rankingResult.inferred_job_profile?.required_skills || [],
      experienceLevel: rankingResult.inferred_job_profile?.experience_level || 'N/A'
    },
    
    // Metadata
    metadata: {
      processingTime: rankingResult.metadata?.processing_time_seconds || 0,
      cost: rankingResult.metadata?.cost_usd || 0,
      inputTokens: rankingResult.metadata?.input_tokens || 0,
      outputTokens: rankingResult.metadata?.output_tokens || 0,
      model: rankingResult.metadata?.model_used || 'Unknown',
      requestId: rankingResult.request_id || null
    }
  };
};

/**
 * Hook-like function for React components to use resume ranking
 * @param {Function} getToken - Clerk getToken function
 * @returns {Object} Object containing rankResume function and utilities
 */
export const useResumeRanking = (getToken) => {
    const analyzeResume = async (resumeId) => {
    return await rankResumeWithErrorHandling(resumeId, getToken);
  };

  const analyzeResumeBasic = async (resumeId) => {
    return await rankResume(resumeId, getToken);
  };

  return {
    analyzeResume,
    analyzeResumeBasic,
    extractInsights: extractResumeInsights
  };
};

// Export all functions as default for convenience
const resumeRankingAPI = {
  rankResume,
  rankResumeWithErrorHandling,
  extractResumeInsights,
  useResumeRanking
};

export default resumeRankingAPI;